import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment, formatServerDateTime } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  TableContainer,
  Paper,
  Grid,
  InputLabel,
  Link,
  Button,
  TextField,
  Card,
  CardContent,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AddIcon from '@mui/icons-material/Add';
import NavigationIcon from '@mui/icons-material/Navigation';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import SourceIcon from '@mui/icons-material/Source';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import GetAppIcon from '@mui/icons-material/GetApp';
import StarIcon from '@mui/icons-material/Star';
import NearMeIcon from '@mui/icons-material/NearMe';
import CircularProgress from '@mui/material/CircularProgress';
import Rating from '@mui/material/Rating';

// Custom components
import { Spinner, AppointmentNotes } from '../../';
import NearestTechModal from '../../Appointments/NearestTechModal';

// Utils
import messages from '../../../static/messages';

// Services
import appointmentsServices from '../../../services/appointmentsServices';
import userServices from '../../../services/userServices';
import proposalsServices from '../../../services/proposalsServices';
import propertiesServices from '../../../services/propertiesServices';
import integrationServices from '../../../services/integrationServices';
import smsServices from '../../../services/smsServices';
import clientsServices from '../../../services/clientsServices';

// Styles
import { flex_center } from '../../../static/styles';
import { button } from '../../../static/theme-styles';

// External variables
import { appointment_times } from '../../Drawers/appointment.drawer';

// Status config for mobile
const statusConfig = {
  'Active': { color: '#4caf50', bgColor: '#e8f5e9', icon: CircleIcon },
  'In Progress': { color: '#ff9800', bgColor: '#fff3e0', icon: CircleIcon },
  'Completed': { color: '#2196f3', bgColor: '#e3f2fd', icon: CheckCircleIcon },
  'Cancelled': { color: '#f44336', bgColor: '#ffebee', icon: CancelIcon },
};

// Mobile Info Row Component
const MobileInfoRow = ({ icon: Icon, label, children, highlight = false }) => (
  <Box sx={{ 
    py: 1.5, 
    px: 2,
    backgroundColor: highlight ? '#fff8e1' : 'transparent',
    borderBottom: '1px solid #eee',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
      {Icon && <Icon sx={{ fontSize: 18, color: '#666', mr: 1 }} />}
      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: 11 }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ pl: Icon ? 3.5 : 0 }}>
      {children}
    </Box>
  </Box>
);

const AppointmentDetailsTable = ({ appointment, setAppointment, id, refresh, isReadonly = false }) => {
  const [userRole, setUserRole] = useState();
  const [nearestTechOpen, setNearestTechOpen] = useState(false);
  const [techniciansList, setTechniciansList] = useState();
  const [types, setTypes] = useState();
  const [sources, setSources] = useState();
  const [updates, setUpdates] = useState({
    isEdited: false,
  });
  const [propertyComment, setPropertyComment] = useState();
  const [proposalTypeDialogOpen, setProposalTypeDialogOpen] = useState(false);
  const [selectedProposalType, setSelectedProposalType] = useState('Installation');
  const [fetchingLegacyNotes, setFetchingLegacyNotes] = useState(false);
  
  // Cancel reason modal states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // Completion rating modal states
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [completionRating, setCompletionRating] = useState(0);
  const [completionClientId, setCompletionClientId] = useState('');
  const [completionSubmitting, setCompletionSubmitting] = useState(false);
  const [completionFromCheckout, setCompletionFromCheckout] = useState(false);

  // Review request modal states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewClientId, setReviewClientId] = useState('');
  const [reviewSending, setReviewSending] = useState(false);
  const [lastReviewDate, setLastReviewDate] = useState(null);
  const [canSendReview, setCanSendReview] = useState(true);
  const [lastReviewLoading, setLastReviewLoading] = useState(false);

  const confirm = useConfirm();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const openCompletionDialog = (fromCheckout = false) => {
    const clients = appointment?.property?.clients || [];
    const isTimeBlock = appointment?.type?.id === 36 || appointment?.type?.name === 'Time Block';

    if (fromCheckout && (isTimeBlock || clients.length === 0)) {
      addTimeStamp('checkOut');
      return;
    }

    const defaultClient = clients.find(cp => cp.client?.id === appointment?.contactClient?.id)?.client
      || clients[0]?.client;
    const defaultClientId = defaultClient?.id || '';
    setCompletionRating(defaultClient?.rating || 0);
    setCompletionClientId(defaultClientId);
    setCompletionFromCheckout(fromCheckout);
    setCompletionDialogOpen(true);
  };

  // Handler functions
  const handleChangeStatus = (event) => {
    const newStatus = event.target.value;
    
    // If changing to Cancelled, open the cancel reason modal
    if (newStatus === 'Cancelled') {
      setCancelReason('');
      setCancelDialogOpen(true);
      return;
    }

    // If changing to Completed, open the completion review modal
    if (newStatus === 'Completed') {
      openCompletionDialog(false);
      return;
    }
    
    confirm({
      title: messages.confirm_message.update,
      description: messages.confirm_message.update_description('status'),
    })
      .then(() => updateStatus(newStatus))
      .catch(() => toast.info(messages.cancel_message));
  };
  
  // Handle cancellation with reason
  const handleCancelWithReason = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please enter a cancellation reason');
      return;
    }
    
    setCancelLoading(true);
    try {
      // Create cancel note (user and timestamp are added automatically by the system)
      const cancelNote = `🚫 CANCELLED: ${cancelReason.trim()}`;
      
      // First update status to Cancelled
      const statusResponse = await appointmentsServices.update(id, { 
        status: 'Cancelled'
      });
      
      if (statusResponse.status !== 200) {
        throw new Error('Failed to update status');
      }
      
      // Then add cancel note separately
      const existingNotes = appointment?.notes || [];
      const notes = [
        ...existingNotes.filter(n => n && n.id).map(n => ({ id: n.id, note: n.note })), // Keep existing notes with both id and note
        { note: cancelNote }, // Add new cancel note
      ];
      
      const response = await appointmentsServices.update(id, { notes });
      
      if (response.status === 200) {
        toast.success('Appointment cancelled successfully');
        setCancelDialogOpen(false);
        setCancelReason('');
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleChange = (data) => {
    setUpdates((prevState) => ({
      ...prevState,
      ...data,
      isEdited: true,
    }));

    setAppointment((prevState) => ({
      ...prevState,
      ...data,
    }));
  };

  const handleChangeDate = (event) => {
    const existingStartTime = appointment?.startTime 
      ? moment.utc(appointment.startTime).tz('America/Los_Angeles').format('HH:mm:ss')
      : '16:00:00';
    const existingEndTime = appointment?.endTime 
      ? moment.utc(appointment.endTime).tz('America/Los_Angeles').format('HH:mm:ss')
      : '17:00:00';
    
    const date = moment(event.$d).format('YYYY-MM-DD');
    const newStartTime = moment.tz(date + ' ' + existingStartTime, 'YYYY-MM-DD HH:mm:ss', 'America/Los_Angeles').utc().format();
    const newEndTime = moment.tz(date + ' ' + existingEndTime, 'YYYY-MM-DD HH:mm:ss', 'America/Los_Angeles').utc().format();

    setUpdates((prevState) => ({
      ...prevState,
      startTime: newStartTime,
      endTime: newEndTime,
      isEdited: true,
    }));

    setAppointment((prevState) => ({
      ...prevState,
      startTime: newStartTime,
      endTime: newEndTime,
    }));
  };

  const handleChangePreferredDate = (event) => {
    const date = moment(event.$d).format('YYYY-MM-DD');
    
    const existingStartHour = appointment?.preferredStartTime 
      ? moment.utc(appointment.preferredStartTime).tz('America/Los_Angeles').format('HH:mm:ss')
      : '16:00:00';
    const existingEndHour = appointment?.preferredEndTime 
      ? moment.utc(appointment.preferredEndTime).tz('America/Los_Angeles').format('HH:mm:ss')
      : '17:00:00';

    const newPreferredDate = moment.tz(date + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', 'America/Los_Angeles').utc().format();
    const newPreferredStartTime = moment.tz(date + ' ' + existingStartHour, 'YYYY-MM-DD HH:mm:ss', 'America/Los_Angeles').utc().format();
    const newPreferredEndTime = moment.tz(date + ' ' + existingEndHour, 'YYYY-MM-DD HH:mm:ss', 'America/Los_Angeles').utc().format();

    setUpdates((prevState) => ({
      ...prevState,
      preferredDate: newPreferredDate,
      preferredStartTime: newPreferredStartTime,
      preferredEndTime: newPreferredEndTime,
      isEdited: true,
    }));

    setAppointment((prevState) => ({
      ...prevState,
      preferredDate: newPreferredDate,
      preferredStartTime: newPreferredStartTime,
      preferredEndTime: newPreferredEndTime,
    }));
  };

  const handleChangePreferredTime = (event) => {
    const key = event.target.name;
    const val = event.target.value;

    const date = appointment.preferredDate 
      ? moment(appointment.preferredDate).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');

    const newTime = moment(date)
      .set({
        hours: val.split(':')[0],
        minutes: val.split(':')[1],
        seconds: '0',
      })
      .utcOffset('-0800');

    setUpdates((prevState) => ({
      ...prevState,
      [key]: moment.utc(newTime).format(),
      isEdited: true,
    }));

    setAppointment((prevState) => ({
      ...prevState,
      [key]: moment.utc(newTime).format(),
    }));
  };

  const handleChangeTime = (event) => {
    const key = event.target.name;
    const val = event.target.value;

    const date = appointment.startTime.split('T')[0];

    const newTime = moment(date)
      .set({
        hours: val.split(':')[0],
        minutes: val.split(':')[1],
        seconds: '0',
      })
      .utcOffset('-0800');

    setUpdates((prevState) => ({
      ...prevState,
      [key]: moment.utc(newTime).format(),
      isEdited: true,
    }));

    setAppointment((prevState) => ({
      ...prevState,
      [key]: moment.utc(newTime).format(),
    }));
  };

  const handleRemove = () => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Appointment'),
    })
      .then(() => removeAppointment())
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleToQueue = () => {
    confirm({
      title: messages.confirm_message.update,
      description: messages.confirm_message.update_description('Appointment'),
    })
      .then(() => backToQueue())
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleDuplicate = () => {
    confirm({
      title: messages.confirm_message.copy,
      description: messages.confirm_message.copy_description('Appointment'),
    })
      .then(() => duplicate())
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleNavigate = () => {
    const address = encodeURIComponent(appointment?.property?.address || '');
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  const handleCall = () => {
    const clients = appointment?.property?.clients || [];
    const contactClient = appointment?.contactClient?.id
      ? clients.find(cp => cp.client?.id === appointment.contactClient.id)?.client || appointment.contactClient
      : clients[0]?.client;
    const phone = contactClient?.phoneNumber || contactClient?.mobile;
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      toast.info('No phone number available');
    }
  };

  // Async functions
  const getTechniciansList = async () => {
    try {
      const response = await userServices.getAssignableUsers(false);

      if (response.status === 206) {
        const assignableRoles = ['technician', 'admin', 'superadmin', 'dispatcher'];
        const filteredUsers = response.data.data
          .filter((user) => assignableRoles.includes(user.role) && !user.deletedAt)
          .map((user) => ({
            tech_id: user.id,
            name: user.fullName || `${user.firstName} ${user.lastName}`,
            color: user.preferences?.color || 'red',
          }));
        setTechniciansList(filteredUsers);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const getTypes = async () => {
    try {
      const response = await appointmentsServices.getAppointmentTypes(false);
      if (response.status === 206) setTypes(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const getSources = async () => {
    try {
      const response = await appointmentsServices.getAppointmentSources(false);
      if (response.status === 206) setSources(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateDetails = async () => {
    try {
      const response = await appointmentsServices.update(id, updates);
      if (response.status === 200) {
        setUpdates({ isEdited: false });
        toast.success(`Appointment details updated successfully`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateStatus = async (status) => {
    try {
      const response = await appointmentsServices.update(id, { status });
      if (response.status === 200) {
        toast.success(`Status successfully updated to ${status}`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const addTimeStamp = async (mode) => {
    const date = moment().utcOffset('-0800');

    try {
      const response = await appointmentsServices.update(appointment?.id, {
        ...(mode === 'checkIn' && { checkIn: moment.utc(date).format() }),
        ...(mode === 'checkOut' && { checkOut: moment.utc(date).format() }),
      });

      if (response.status === 200) {
        toast.success(mode === 'checkIn' ? '✓ Checked In!' : '✓ Checked Out!');
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeAppointment = async () => {
    try {
      const response = await appointmentsServices.remove(id);
      if (response.status === 200) {
        toast.success(messages.appointment.remove);
        navigate('/appointments');
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const backToQueue = async () => {
    try {
      const response = await appointmentsServices.update(id, {
        status: 'Active',
        startTime: null,
        endTime: null,
        user: { id: null },
      });
      if (response.status === 200) {
        toast.success('Appointment restored to queue');
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const duplicate = async () => {
    const data = {
      status: 'Active',
      ...(appointment.type && { type: { id: appointment.type.id } }),
      ...(appointment.source && { source: { id: appointment.source.id } }),
      ...(appointment.property && { property: { id: appointment.property.id } }),
    };

    try {
      const response = await appointmentsServices.create(data);
      if (response.status === 201) navigate('/appointments');
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const handleOpenProposalTypeDialog = () => {
    console.log('[DEBUG] handleOpenProposalTypeDialog called');
    setSelectedProposalType('Installation');
    setProposalTypeDialogOpen(true);
    console.log('[DEBUG] proposalTypeDialogOpen set to true');
  };

  const handleCloseProposalTypeDialog = () => {
    setProposalTypeDialogOpen(false);
  };

  const handleProposalTypeChange = (event, newType) => {
    if (newType !== null) {
      setSelectedProposalType(newType);
    }
  };

  const createProposal = async () => {
    console.log('[DEBUG] createProposal called');
    setProposalTypeDialogOpen(false);
    
    const clients = appointment?.property?.clients || [];
    const contactClient = appointment?.contactClient?.id
      ? clients.find(cp => cp.client?.id === appointment.contactClient.id)?.client
      : clients[0]?.client;
    const clientId = contactClient?.id;
    console.log('[DEBUG] appointment:', appointment);
    console.log('[DEBUG] property:', appointment?.property);
    console.log('[DEBUG] clientId:', clientId);
    
    const data = {
      property: { id: appointment?.property?.id },
      proposalType: selectedProposalType,
      status: 'Active',
    };
    
    // Only add client if exists
    if (clientId) {
      data.client = { id: clientId };
      data.emailTo = [clientId];
    }

    console.log('[DEBUG] Creating proposal with data:', data);

    try {
      const response = await proposalsServices.create(data);
      console.log('[DEBUG] Response:', response);
      if (response.status === 200 || response.status === 201) {
        navigate(`/proposals/${response.data.id}`);
      }
    } catch (error) {
      console.error('[DEBUG] Error creating proposal:', error);
      toast.error(messages.errors.error_try_again);
    }
  };

  const addPropertyNote = async (noteContent) => {
    // If noteContent is event object or not a string, use propertyComment
    const content = (typeof noteContent === 'string') ? noteContent : propertyComment;
    if (!content || content.trim() === "") return;
    
    if (!appointment?.property?.id) {
      toast.error('No property found for this appointment');
      return;
    }

    try {
      const response = await propertiesServices.addPropertyNote(appointment.property.id, content);
      if (response.status === 200 || response.status === 201) {
        setPropertyComment('');
        toast.success(messages.success.update.note.save);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const handleGetLegacyNotes = async () => {
    const address = appointment?.property?.address;
    if (!address) {
      toast.error('No property address available');
      return;
    }

    setFetchingLegacyNotes(true);
    try {
      const response = await integrationServices.getLegacyPropertyNotes(address);
      if (response.data.found && response.data.notes) {
        const legacyNote = `[LEGACY CRM NOTES]\n${response.data.notes}`;
        await addPropertyNote(legacyNote);
        toast.success('Legacy notes imported successfully!');
      } else {
        toast.info('No legacy notes found for this address.');
      }
    } catch (error) {
      console.error('Failed to fetch legacy notes:', error);
      toast.error('Failed to fetch legacy notes.');
    } finally {
      setFetchingLegacyNotes(false);
    }
  };

  const handleCompletionSubmit = async () => {
    if (completionRating === 0) return;

    const clients = appointment?.property?.clients || [];
    const selectedCp = clients.find((cp) => cp.client?.id === completionClientId);
    const client = selectedCp?.client;

    setCompletionSubmitting(true);
    try {
      if (client?.id) {
        await clientsServices.update(client.id, { rating: completionRating });
      }

      if (completionFromCheckout) {
        const date = moment().utcOffset('-0800');
        const response = await appointmentsServices.update(appointment?.id, {
          checkOut: moment.utc(date).format(),
        });
        if (response.status !== 200) {
          throw new Error('Failed to check out');
        }
        toast.success('✓ Checked Out! Rating saved.');
      } else {
        const statusResponse = await appointmentsServices.update(id, { status: 'Completed' });
        if (statusResponse.status !== 200) {
          throw new Error('Failed to update status');
        }
        toast.success('Status updated to Completed. Rating saved.');
      }

      setCompletionDialogOpen(false);
      refresh();
    } catch (error) {
      console.error('Failed to complete appointment:', error);
      toast.error('Failed to update appointment status');
    } finally {
      setCompletionSubmitting(false);
    }
  };

  const fetchLastReviewDate = async (clientId) => {
    if (!clientId) return;
    setLastReviewLoading(true);
    try {
      const res = await smsServices.getLastReviewRequest(clientId);
      setLastReviewDate(res.data?.lastSentAt || null);
      setCanSendReview(res.data?.canSendReview ?? true);
    } catch (e) {
      console.warn('Failed to fetch last review date:', e);
      setLastReviewDate(null);
      setCanSendReview(true);
    } finally {
      setLastReviewLoading(false);
    }
  };

  const handleOpenReviewDialog = () => {
    const clients = appointment?.property?.clients || [];
    const defaultClient = clients.find(cp => cp.client?.id === appointment?.contactClient?.id)?.client
      || clients[0]?.client;
    const defaultClientId = defaultClient?.id || '';
    setReviewClientId(defaultClientId);
    setReviewDialogOpen(true);
    fetchLastReviewDate(defaultClientId);
  };

  const handleReviewClientChange = (newClientId) => {
    setReviewClientId(newClientId);
    fetchLastReviewDate(newClientId);
  };

  const handleSendReview = async () => {
    const clients = appointment?.property?.clients || [];
    const selectedCp = clients.find((cp) => cp.client?.id === reviewClientId);
    const client = selectedCp?.client;

    if (!client?.phoneNumber) {
      toast.warning(`${client?.fullName || 'Client'} has no phone number`);
      return;
    }

    setReviewSending(true);
    try {
      const res = await smsServices.sendReviewRequest({
        clientPhone: client.phoneNumber,
        clientName: client.firstName || client.fullName || '',
        appointmentId: appointment?.id,
        clientId: client.id,
      });
      if (res.data?.success) {
        toast.success(`Review request sent to ${client.fullName}`);
        setReviewDialogOpen(false);
        refresh();
      } else {
        toast.info(res.data?.message || 'Review SMS was not sent');
      }
    } catch (error) {
      console.error('Failed to send review:', error);
      toast.error('Failed to send review request');
    } finally {
      setReviewSending(false);
    }
  };

  useEffect(() => {
    getTechniciansList();
    getTypes();
    getSources();
    const role = JSON.parse(localStorage.getItem('user')).role;
    setUserRole(role);
  }, []);

  if (!techniciansList && !types && !sources && !userRole) return <Spinner />;

  const isInQueue = !appointment?.startTime || !appointment?.endTime;
  const isCompleted = appointment?.status === 'Completed';
  const canDispatch = ['dispatcher', 'admin', 'superadmin'].includes(userRole);
  const status = statusConfig[appointment?.status] || statusConfig['Active'];
  const StatusIcon = status.icon;

  // ===================== MOBILE VIEW =====================
  if (isMobile) {
    return (
      <Box sx={{ pb: 4 }}>
        {/* Header Card with Address & Quick Actions */}
        <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ pb: 2 }}>
            {/* Status Badge */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isCompleted && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<StarIcon sx={{ fontSize: 14 }} />}
                    onClick={handleOpenReviewDialog}
                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#ff9800', color: '#ff9800', fontSize: 12, py: 0.5, borderRadius: 2 }}
                  >
                    Review
                  </Button>
                )}
                <Chip
                  icon={<StatusIcon sx={{ fontSize: 14, color: status.color + ' !important' }} />}
                  label={appointment?.status}
                  size="small"
                  sx={{
                    backgroundColor: status.bgColor,
                    color: status.color,
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: status.color },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={appointment?.status}
                    onChange={handleChangeStatus}
                    disabled={isReadonly}
                    size="small"
                    sx={{ fontSize: 12 }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Address */}
            <Box sx={{ mb: 2 }}>
              {appointment?.property ? (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                    {appointment.property.address}
                  </Typography>
                  {appointment.property.unit && (
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Unit: {appointment.property.unit}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5, color: '#999' }}>
                  No property linked
                </Typography>
              )}
            </Box>

            {/* Quick Action Buttons */}
            {appointment?.property && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<NavigationIcon />}
                onClick={handleNavigate}
                sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}
              >
                Navigate
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhoneIcon />}
                onClick={handleCall}
                sx={{ flex: 1, borderRadius: 2, textTransform: 'none', borderColor: '#4caf50', color: '#4caf50' }}
              >
                Call
              </Button>
              <IconButton
                onClick={() => navigate(`/properties/${appointment.property.id}`)}
                sx={{ border: '1px solid #1976d2', borderRadius: 2 }}
              >
                <HomeIcon sx={{ color: '#1976d2' }} />
              </IconButton>
            </Box>
            )}

            {appointment?.property && canDispatch && (
              <Button
                variant="outlined"
                fullWidth
                startIcon={<NearMeIcon />}
                onClick={() => setNearestTechOpen(true)}
                sx={{ mb: 2, borderRadius: 2, textTransform: 'none' }}
              >
                Nearest Tech
              </Button>
            )}

            {/* Check In / Check Out */}
            <Box>
              {!appointment?.checkIn ? (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<TimerIcon />}
                  onClick={() => addTimeStamp('checkIn')}
                  disabled={!appointment.user || isReadonly}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 16,
                    backgroundColor: '#4caf50',
                    '&:hover': { backgroundColor: '#43a047' },
                  }}
                >
                  Check In
                </Button>
              ) : !appointment?.checkOut ? (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<TimerOffIcon />}
                  onClick={() => openCompletionDialog(true)}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 16,
                    backgroundColor: '#ff9800',
                    '&:hover': { backgroundColor: '#f57c00' },
                  }}
                >
                  Check Out
                </Button>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 1.5,
                  backgroundColor: '#e3f2fd',
                  borderRadius: 2,
                }}>
                  <Typography sx={{ color: '#1976d2', fontWeight: 600 }}>
                    ✓ Completed at {moment.utc(appointment.checkOut).tz('America/Los_Angeles').format('h:mm A')}
                  </Typography>
                </Box>
              )}
            </Box>

          </CardContent>
        </Card>

        {/* Appointment Info Card */}
        <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #eee' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              📋 Appointment Info
            </Typography>
          </Box>

          {/* Preferred Date */}
          <MobileInfoRow icon={EventIcon} label="Preferred Date" highlight>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                inputFormat="MM/DD/YYYY"
                value={appointment?.preferredDate ? moment.utc(appointment.preferredDate).tz('America/Los_Angeles').format('MM/DD/YYYY') : null}
                onChange={handleChangePreferredDate}
                disabled={userRole === 'technician' || isReadonly}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size="small" 
                    error={false}
                    placeholder="Select date"
                    sx={{ backgroundColor: '#fff' }}
                  />
                )}
              />
            </LocalizationProvider>
          </MobileInfoRow>

          {/* Preferred Time Range */}
          <MobileInfoRow icon={AccessTimeIcon} label="Preferred Time" highlight>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Select
                name="preferredStartTime"
                value={
                  appointment?.preferredStartTime
                    ? appointment_times.filter(
                        (el) => el.time12 === moment.utc(appointment.preferredStartTime).tz('America/Los_Angeles').format('hh:mm A'),
                      )[0]?.time24 || ''
                    : ''
                }
                onChange={handleChangePreferredTime}
                size="small"
                fullWidth
                disabled={userRole === 'technician' || !appointment?.preferredDate || isReadonly}
                displayEmpty
                sx={{ backgroundColor: '#fff' }}
              >
                <MenuItem value=""><em>Start</em></MenuItem>
                {appointment_times.map((time, index) => (
                  <MenuItem key={index} value={time.time24}>{time.time12}</MenuItem>
                ))}
              </Select>
              <Typography sx={{ alignSelf: 'center' }}>-</Typography>
              <Select
                name="preferredEndTime"
                value={
                  appointment?.preferredEndTime
                    ? appointment_times.filter(
                        (el) => el.time12 === moment.utc(appointment.preferredEndTime).tz('America/Los_Angeles').format('hh:mm A'),
                      )[0]?.time24 || ''
                    : ''
                }
                onChange={handleChangePreferredTime}
                size="small"
                fullWidth
                disabled={userRole === 'technician' || !appointment?.preferredDate || isReadonly}
                displayEmpty
                sx={{ backgroundColor: '#fff' }}
              >
                <MenuItem value=""><em>End</em></MenuItem>
                {appointment_times.map((time, index) => (
                  <MenuItem key={index} value={time.time24}>{time.time12}</MenuItem>
                ))}
              </Select>
            </Box>
          </MobileInfoRow>

          {/* Preferred Technician */}
          <MobileInfoRow icon={PersonIcon} label="Preferred Technician" highlight>
            <Select
              name="preferredUser"
              value={appointment?.preferredUser?.id || ''}
              onChange={(e) => handleChange({ 
                preferredUser: { id: e.target.value || null } 
              })}
              size="small"
              fullWidth
              disabled={userRole === 'technician' || isReadonly}
              displayEmpty
              sx={{ backgroundColor: '#fff' }}
            >
              <MenuItem value=""><em>Not set</em></MenuItem>
              {techniciansList?.map((tech) => (
                <MenuItem key={tech.tech_id} value={tech.tech_id}>{tech.name}</MenuItem>
              ))}
            </Select>
          </MobileInfoRow>

          <Divider />

          {/* Assigned Technician */}
          <MobileInfoRow icon={PersonIcon} label="Assigned Technician">
            <Select
              value={appointment?.user?.id || ''}
              onChange={(event) => handleChange({ user: { id: event.target.value } })}
              size="small"
              fullWidth
              disabled={userRole === 'technician' || isReadonly}
              displayEmpty
              sx={{ backgroundColor: '#fff' }}
            >
              <MenuItem value=""><em>Not assigned</em></MenuItem>
              {techniciansList?.map((tech) => (
                <MenuItem key={tech.tech_id} value={tech.tech_id}>{tech.name}</MenuItem>
              ))}
            </Select>
          </MobileInfoRow>

          {/* Client */}
          <MobileInfoRow icon={PersonIcon} label="Client">
            {(() => {
              const clients = appointment?.property?.clients || [];
              const displayClient = appointment?.contactClient?.id
                ? clients.find(cp => cp.client?.id === appointment.contactClient.id)?.client || appointment.contactClient
                : clients[0]?.client;
              if (!displayClient) return <Typography variant="body2" sx={{ color: '#999' }}>No client linked</Typography>;
              return (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Link 
                      href={`/clients/${displayClient.id}`}
                      underline="hover"
                      sx={{ fontWeight: 600, color: '#1976d2' }}
                    >
                      {displayClient.fullName || `${displayClient.firstName || ''} ${displayClient.lastName || ''}`.trim() || "No name"}
                    </Link>
                    {displayClient.rating > 0 && (
                      <Rating value={displayClient.rating} size="small" readOnly />
                    )}
                  </Box>
                  {displayClient.phoneNumber && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: '#999', mr: 0.5 }} />
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {displayClient.phoneNumber}
                      </Typography>
                    </Box>
                  )}
                </>
              );
            })()}
          </MobileInfoRow>

          {/* Appointment Type */}
          <MobileInfoRow icon={BuildIcon} label="Appointment Type">
            <Select
              value={appointment?.type?.id || ''}
              onChange={(event) => handleChange({ type: { id: event.target.value } })}
              size="small"
              fullWidth
              disabled={userRole === 'technician' || isReadonly}
              sx={{ backgroundColor: '#fff' }}
            >
              {types?.map((type) => (
                <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
              ))}
            </Select>
          </MobileInfoRow>

          {/* Source */}
          <MobileInfoRow icon={SourceIcon} label="Source">
            <Select
              value={appointment?.source?.id || ''}
              onChange={(event) => handleChange({ source: { id: event.target.value } })}
              size="small"
              fullWidth
              disabled={userRole === 'technician' || isReadonly}
              sx={{ backgroundColor: '#fff' }}
            >
              {sources?.map((source) => (
                <MenuItem key={source.id} value={source.id}>{source.name}</MenuItem>
              ))}
            </Select>
          </MobileInfoRow>

          {/* Created By */}
          <MobileInfoRow label="Created By">
            <Typography variant="body2">
              {appointment?.createdBy?.fullName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {formatServerDateTime(appointment?.createdAt, 'L, h:mm A')}
            </Typography>
          </MobileInfoRow>

          {isInQueue && (
            <Box sx={{ p: 2, backgroundColor: '#fff3e0' }}>
              <Typography sx={{ color: '#e65100', fontWeight: 600, textAlign: 'center' }}>
                📋 In Queue - Drag to calendar to schedule
              </Typography>
            </Box>
          )}
        </Card>

        {/* Save Button (sticky at bottom when edited) */}
        {updates.isEdited && (
          <Box sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            p: 2, 
            backgroundColor: '#fff',
            boxShadow: '0 -2px 12px rgba(0,0,0,0.1)',
            zIndex: 100,
          }}>
            <Button
              variant="contained"
              fullWidth
              onClick={updateDetails}
              sx={{
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 16,
              }}
            >
              Save Changes
            </Button>
          </Box>
        )}

        {/* Notes Section */}
        <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #eee' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              📝 Appointment Notes
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <AppointmentNotes notesList={appointment?.notes} id={id} refresh={refresh} />
          </Box>
        </Card>

        {/* Property Notes */}
        {appointment?.property && (
        <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              🏠 Property Notes
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleGetLegacyNotes}
              disabled={fetchingLegacyNotes}
              startIcon={fetchingLegacyNotes ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
            >
              Legacy
            </Button>
          </Box>
          <Box sx={{ p: 2 }}>
            {appointment.property.notes?.length > 0 ? (
              appointment.property.notes.map((el) => (
                <Box key={el.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{el.note}</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {el.createdBy?.fullName} • {formatServerDateTime(el.createdAt, 'L')}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 2 }}>
                No property notes
              </Typography>
            )}

            <TextField
              label="Add Note"
              multiline
              rows={3}
              value={propertyComment || ''}
              fullWidth
              onChange={(e) => setPropertyComment(e.target.value)}
              sx={{ mt: 2 }}
            />
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2, borderRadius: 2 }}
              onClick={addPropertyNote}
              disabled={!propertyComment}
            >
              Add Note
            </Button>
          </Box>
        </Card>
        )}

        {/* Action Buttons */}
        <Card sx={{ mb: 10, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #eee' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              ⚡ Actions
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              {(userRole === 'technician' || userRole === 'superadmin') && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={handleOpenProposalTypeDialog}
                  sx={{ borderRadius: 2, backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#43a047' } }}
                >
                  Create Proposal
                </Button>
              )}

              {(userRole === 'superadmin' || userRole === 'admin' || userRole === 'dispatcher') && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<FileCopyIcon />}
                  onClick={handleDuplicate}
                  sx={{ borderRadius: 2 }}
                >
                  Duplicate Appointment
                </Button>
              )}

              {userRole === 'superadmin' && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleToQueue}
                  disabled={isInQueue}
                  sx={{ borderRadius: 2 }}
                >
                  Put Back In Queue
                </Button>
              )}

              {(userRole === 'superadmin' || userRole === 'admin') && (
                <Button
                  variant="outlined"
                  fullWidth
                  color="error"
                  onClick={handleRemove}
                  sx={{ borderRadius: 2 }}
                >
                  Delete Appointment
                </Button>
              )}
            </Stack>
          </Box>
        </Card>

        {/* Proposal Type Selection Dialog - Mobile */}
        <Dialog 
          open={proposalTypeDialogOpen} 
          onClose={handleCloseProposalTypeDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            Select Proposal Type
          </DialogTitle>
          <DialogContent>
            <ToggleButtonGroup
              value={selectedProposalType}
              exclusive
              onChange={handleProposalTypeChange}
              aria-label="proposal type"
              fullWidth
              sx={{ mt: 1 }}
            >
              <ToggleButton 
                value="Installation" 
                aria-label="installation"
                sx={{
                  py: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#1976d2',
                    color: 'white',
                    '&:hover': { backgroundColor: '#1565c0' },
                  },
                }}
              >
                <HomeWorkIcon sx={{ mr: 1 }} />
                Installation
              </ToggleButton>
              <ToggleButton 
                value="Service" 
                aria-label="service"
                sx={{
                  py: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#ed6c02',
                    color: 'white',
                    '&:hover': { backgroundColor: '#e65100' },
                  },
                }}
              >
                <BuildIcon sx={{ mr: 1 }} />
                Service
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
              {selectedProposalType === 'Installation' 
                ? 'For equipment installation and replacement projects'
                : 'For maintenance and repair services'
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseProposalTypeDialog} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={createProposal} 
              variant="contained"
              sx={{ 
                backgroundColor: selectedProposalType === 'Installation' ? '#1976d2' : '#ed6c02',
                '&:hover': {
                  backgroundColor: selectedProposalType === 'Installation' ? '#1565c0' : '#e65100',
                },
              }}
            >
              Create {selectedProposalType} Proposal
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Reason Dialog - Mobile */}
        <Dialog 
          open={cancelDialogOpen} 
          onClose={() => !cancelLoading && setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            textAlign: 'center', 
            pb: 1,
            color: '#d32f2f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <CancelIcon />
            Cancel Appointment
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Please provide a reason for cancelling this appointment.
            </Typography>
            <TextField
              autoFocus
              label="Cancellation Reason"
              multiline
              rows={4}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth
              placeholder="Enter the reason for cancellation..."
              disabled={cancelLoading}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1 }}>
            <Button 
              onClick={handleCancelWithReason} 
              variant="contained"
              color="error"
              fullWidth
              disabled={!cancelReason.trim() || cancelLoading}
              startIcon={cancelLoading ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
            >
              {cancelLoading ? 'Cancelling...' : 'Cancel Appointment'}
            </Button>
            <Button 
              onClick={() => setCancelDialogOpen(false)} 
              color="inherit"
              fullWidth
              disabled={cancelLoading}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>

        {/* Completion Rating Dialog (mobile) */}
        <Dialog
          open={completionDialogOpen}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: '#ff9800' }} />
            {completionFromCheckout ? 'Check Out — Rate Client' : 'Complete — Rate Client'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              {completionFromCheckout
                ? 'Please rate this client before checking out.'
                : 'Please rate this client before completing the appointment.'}
            </Typography>

            {(() => {
              const selectedCp = (appointment?.property?.clients || []).find(cp => cp.client?.id === completionClientId);
              const selectedClient = selectedCp?.client;
              const existingRating = selectedClient?.rating || 0;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
                    {selectedClient?.fullName || 'Client'}
                  </Typography>
                  {existingRating > 0 && (
                    <Typography variant="caption" sx={{ mb: 1, color: '#999' }}>
                      Current rating: {existingRating}/5
                    </Typography>
                  )}
                  <Rating
                    value={completionRating}
                    onChange={(e, newValue) => setCompletionRating(newValue || 0)}
                    size="large"
                    sx={{ fontSize: 36 }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, color: completionRating > 0 ? '#4caf50' : '#999', textAlign: 'center' }}>
                    {completionRating === 0 ? 'Select a rating to continue' : `Rating: ${completionRating}/5`}
                  </Typography>
                </Box>
              );
            })()}

            {(appointment?.property?.clients || []).length > 1 && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="completion-client-mobile-label">Rate client</InputLabel>
                <Select
                  labelId="completion-client-mobile-label"
                  value={completionClientId}
                  label="Rate client"
                  onChange={(e) => {
                    const newId = e.target.value;
                    setCompletionClientId(newId);
                    const cp = (appointment?.property?.clients || []).find(c => c.client?.id === newId);
                    setCompletionRating(cp?.client?.rating || 0);
                  }}
                >
                  {(appointment?.property?.clients || []).map((cp) => (
                    <MenuItem key={cp.client?.id} value={cp.client?.id}>
                      {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                      {cp.client?.id === appointment?.contactClient?.id ? ' (preferred)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleCompletionSubmit}
              disabled={completionRating === 0 || completionSubmitting}
              fullWidth
              startIcon={completionSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#388e3c' },
                '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
              }}
            >
              {completionSubmitting ? 'Processing...' : completionRating > 0
                ? (completionFromCheckout ? 'Check Out & Complete' : 'Complete Appointment')
                : 'Rate to Continue'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Review Request Dialog (mobile) */}
        <Dialog
          open={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: '#ff9800' }} />
            Request Review
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              Send a Google review request via SMS to the selected client.
            </Typography>

            {(appointment?.property?.clients || []).length > 1 && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="review-client-mobile-label">Send review to</InputLabel>
                <Select
                  labelId="review-client-mobile-label"
                  value={reviewClientId}
                  label="Send review to"
                  onChange={(e) => handleReviewClientChange(e.target.value)}
                >
                  {(appointment?.property?.clients || []).map((cp) => (
                    <MenuItem key={cp.client?.id} value={cp.client?.id}>
                      {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                      {cp.client?.id === appointment?.contactClient?.id ? ' (preferred)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {lastReviewLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ color: '#999' }}>Checking review history...</Typography>
              </Box>
            ) : lastReviewDate ? (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: canSendReview ? '#fff3e0' : '#ffebee', borderRadius: 2, border: `1px solid ${canSendReview ? '#ffe0b2' : '#ffcdd2'}` }}>
                <Typography variant="body2" sx={{ color: canSendReview ? '#e65100' : '#c62828', fontWeight: 500 }}>
                  Last review request sent: {new Date(lastReviewDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
                {!canSendReview && (
                  <Typography variant="caption" sx={{ color: '#c62828', display: 'block', mt: 0.5 }}>
                    Next request allowed after 6 months from the last one.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                  No previous review requests for this client.
                </Typography>
              </Box>
            )}

            <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>
                SMS preview:
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: 'italic', lineHeight: 1.5, color: '#333' }}>
                Airmaxx is family owned, so your feedback goes a long way! If you have a moment, please share your experience: https://g.page/r/CUnMmOhln4AMEBM/review If you have any concerns, please call 619-655-3010. Thank you!
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSendReview}
              disabled={reviewSending || !canSendReview}
              startIcon={reviewSending ? <CircularProgress size={16} color="inherit" /> : <StarIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#ff9800',
                '&:hover': { backgroundColor: '#f57c00' },
                '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
              }}
            >
              {reviewSending ? 'Sending...' : 'Send Review Request'}
            </Button>
            <Button fullWidth onClick={() => setReviewDialogOpen(false)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <NearestTechModal
          open={nearestTechOpen}
          onClose={() => setNearestTechOpen(false)}
          appointmentId={id}
          address={appointment?.property?.address}
        />
      </Box>
    );
  }

  // ===================== DESKTOP VIEW (unchanged) =====================
  return (
    <Fragment>
      <Box
        sx={{
          ...flex_center,
          mt: 2,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start !important', md: 'center !important' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {appointment?.property ? (
            <>
              <Link href={`/properties/${appointment.property.id}`} underline="hover" color="rgba(0, 0, 0, 0.87)">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {appointment.property.address}
                </Typography>
              </Link>
              <Link
                href={`https://www.google.com/maps/place/${appointment.property.address || ''}`}
                variant="contained"
                sx={{ ml: 2 }}
                target="_blank"
              >
                Navigate To Location
              </Link>
              {canDispatch && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<NearMeIcon />}
                  onClick={() => setNearestTechOpen(true)}
                  sx={{ ml: 2, textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Nearest Tech
                </Button>
              )}
            </>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#999' }}>
              No property linked
            </Typography>
          )}

          {!appointment?.checkIn && (
            <Button
              variant="contained"
              onClick={() => addTimeStamp('checkIn')}
              size="large"
              sx={{ ...button('secondary'), ml: 2 }}
              endIcon={<TimerIcon />}
              disabled={!appointment.user || isReadonly}
            >
              Check In
            </Button>
          )}

          {appointment?.checkIn && !appointment?.checkOut && (
            <Button
              variant="contained"
              onClick={() => openCompletionDialog(true)}
              size="large"
              sx={{ ...button('secondary'), ml: 2 }}
              disabled={isReadonly}
              endIcon={<TimerOffIcon />}
            >
              Check Out
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: { xs: 3, md: 0 } }}>
          {isCompleted && (
            <Button
              variant="outlined"
              startIcon={<StarIcon />}
              onClick={handleOpenReviewDialog}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#ff9800', color: '#ff9800' }}
            >
              Request Review
            </Button>
          )}
          <FormControl sx={{ minWidth: 120, maxWidth: 150 }} size="small">
            <InputLabel id="status">Status</InputLabel>
            <Select
              labelId="status"
              variant="outlined"
              value={appointment?.status}
              name="status"
              onChange={handleChangeStatus}
              disabled={isReadonly}
              label="Status"
              sx={{ '& > div': { ...flex_center } }}
            >
              <MenuItem value="Active">
                <CircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} /> Active
              </MenuItem>
              <MenuItem value="In Progress">
                <CircleIcon sx={{ color: 'orange', mr: 1, width: 15, height: 15 }} /> In Progress
              </MenuItem>
              <MenuItem value="Completed">
                <CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} /> Completed
              </MenuItem>
              <MenuItem value="Cancelled">
                <CancelIcon sx={{ color: 'red', mr: 1, width: 15, height: 15 }} /> Cancelled
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Appointment Info:</Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {appointment?.property?.unit && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '50%' }}>Unit</TableCell>
                    <TableCell>{appointment?.property?.unit}</TableCell>
                  </TableRow>
                )}

                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>
                    📅 Preferred Date
                    <Typography variant="caption" display="block" color="text.secondary">
                      Customer's requested date
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        inputFormat="MM/DD/YYYY"
                        value={appointment?.preferredDate ? moment.utc(appointment.preferredDate).tz('America/Los_Angeles').format('MM/DD/YYYY') : null}
                        onChange={handleChangePreferredDate}
                        disabled={userRole === 'technician' || isReadonly}
                        renderInput={(params) => (
                          <TextField {...params} name="preferredDate" fullWidth error={false} size="small" />
                        )}
                      />
                    </LocalizationProvider>
                  </TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Preferred Start Time</TableCell>
                  <TableCell>
                    <Select
                      name="preferredStartTime"
                      value={
                        appointment?.preferredStartTime
                          ? appointment_times.filter(
                              (el) => el.time12 === moment.utc(appointment.preferredStartTime).tz('America/Los_Angeles').format('hh:mm A'),
                            )[0]?.time24 || ''
                          : ''
                      }
                      onChange={handleChangePreferredTime}
                      size="small"
                      fullWidth
                      disabled={userRole === 'technician' || !appointment?.preferredDate || isReadonly}
                      displayEmpty
                    >
                      <MenuItem value=""><em>Not set</em></MenuItem>
                      {appointment_times.map((time, index) => (
                        <MenuItem key={index} value={time.time24}>{time.time12}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Preferred End Time</TableCell>
                  <TableCell>
                    <Select
                      name="preferredEndTime"
                      value={
                        appointment?.preferredEndTime
                          ? appointment_times.filter(
                              (el) => el.time12 === moment.utc(appointment.preferredEndTime).tz('America/Los_Angeles').format('hh:mm A'),
                            )[0]?.time24 || ''
                          : ''
                      }
                      onChange={handleChangePreferredTime}
                      size="small"
                      fullWidth
                      disabled={userRole === 'technician' || !appointment?.preferredDate || isReadonly}
                      displayEmpty
                    >
                      <MenuItem value=""><em>Not set</em></MenuItem>
                      {appointment_times.map((time, index) => (
                        <MenuItem key={index} value={time.time24}>{time.time12}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>

                <TableRow sx={{ backgroundColor: '#ffebee' }}>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>
                    👤 Preferred Technician
                    <Typography variant="caption" display="block" color="text.secondary">
                      Customer's requested technician
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      name="preferredUser"
                      value={appointment?.preferredUser?.id || ''}
                      onChange={(e) => handleChange({ preferredUser: { id: e.target.value || null } })}
                      size="small"
                      fullWidth
                      disabled={userRole === 'technician' || isReadonly}
                      displayEmpty
                    >
                      <MenuItem value=""><em>Not set</em></MenuItem>
                      {techniciansList?.map((tech) => (
                        <MenuItem key={tech.tech_id} value={tech.tech_id}>{tech.name}</MenuItem>
                      ))}
                    </Select>
                    {appointment?.preferredUser && appointment?.user && 
                      appointment.preferredUser.id !== appointment.user.id && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
                        ⚠️ Assigned technician doesn't match preference
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>
                    👤 Client
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const clients = appointment?.property?.clients || [];
                      const displayClient = appointment?.contactClient?.id
                        ? clients.find(cp => cp.client?.id === appointment.contactClient.id)?.client || appointment.contactClient
                        : clients[0]?.client;
                      if (!displayClient) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No client linked</Typography>;
                      return (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Link 
                              href={`/clients/${displayClient.id}`}
                              underline="hover"
                              color="rgba(0, 0, 0, 0.87)"
                              sx={{ fontWeight: 600 }}
                            >
                              {displayClient.fullName || `${displayClient.firstName || ''} ${displayClient.lastName || ''}`.trim() || "No name"}
                            </Link>
                            {displayClient.rating > 0 && (
                              <Rating value={displayClient.rating} size="small" readOnly />
                            )}
                          </Box>
                          {displayClient.phoneNumber && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              📞 {displayClient.phoneNumber}
                            </Typography>
                          )}
                          {displayClient.email && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              ✉️ {displayClient.email}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>
                </TableRow>

                {isInQueue && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '50%' }}>Status</TableCell>
                    <TableCell>
                      <Typography color="warning.main" sx={{ fontWeight: 600 }}>
                        📋 In Queue - Drag to calendar to schedule
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Created By</TableCell>
                  <TableCell>
                    {appointment?.createdBy?.fullName} ({formatServerDateTime(appointment?.createdAt, 'L, h:mm:ss a')})
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {appointment?.property && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Property Notes:</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleGetLegacyNotes}
                disabled={fetchingLegacyNotes}
                startIcon={fetchingLegacyNotes ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
              >
                Get Legacy Notes
              </Button>
            </Box>
            {(appointment.property.notes || []).map((el) => (
              <Fragment key={el.id}>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {el.note}
                  </Typography>
                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#666', display: 'block', mt: 0.5 }}>
                    (Created by <strong>{el.createdBy?.fullName || 'Unknown'}</strong> at {formatServerDateTime(el.createdAt)})
                  </Typography>
                </Box>
                <Divider sx={{ mt: 1 }} />
              </Fragment>
            ))}

            <Box sx={{ mt: 3 }}>
              <TextField
                label="New Note"
                multiline
                rows={4}
                value={propertyComment || ''}
                fullWidth
                onChange={(e) => setPropertyComment(e.target.value)}
              />
              <Button
                variant="contained"
                size="small"
                sx={{ ...button('secondary'), mt: 3 }}
                onClick={addPropertyNote}
                disabled={!propertyComment}
              >
                Add Note
              </Button>
            </Box>
          </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6} sx={{ mt: 4 }}>
          {/* Preferred Technician field hidden - moved to left column in Appointment Info table */}

          {types && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="type">Appointment Type</InputLabel>
              <Select
                labelId="type"
                name="type"
                value={appointment?.type?.id || ''}
                label="Appointment Type"
                onChange={(event) => handleChange({ type: { id: event.target.value } })}
                disabled={userRole === 'technician' || isReadonly}
              >
                {types.map((type) => (
                  <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {sources && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="source">Appointment Source</InputLabel>
              <Select
                labelId="source"
                name="source"
                value={appointment?.source?.id || ''}
                label="Appointment Source"
                onChange={(event) => handleChange({ source: { id: event.target.value } })}
                disabled={userRole === 'technician' || isReadonly}
              >
                {sources.map((source) => (
                  <MenuItem key={source.id} value={source.id}>{source.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ mt: 2 }}>
            <AppointmentNotes notesList={appointment?.notes} id={id} refresh={refresh} />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={updateDetails}
            disabled={!updates.isEdited}
            size="large"
            sx={button('primary', 'secondary')}
          >
            Save
          </Button>

          <Button
            variant="contained"
            onClick={handleToQueue}
            size="large"
            sx={{ ...button('secondary'), ml: { md: 2, xs: 1 } }}
            disabled={isInQueue || userRole !== 'superadmin'}
          >
            Put Back In Queue
          </Button>

          {(userRole === 'superadmin' || userRole === 'admin' || userRole === 'dispatcher') && (
            <Button
              variant="contained"
              onClick={() => handleDuplicate(appointment.id)}
              size="large"
              sx={{ ...button('secondary'), ml: { md: 2, xs: 0 } }}
              endIcon={<FileCopyIcon />}
            >
              Duplicate
            </Button>
          )}

          {(userRole === 'technician' || userRole === 'superadmin') && (
            <Button
              variant="contained"
              onClick={handleOpenProposalTypeDialog}
              size="large"
              sx={{ ...button('green', 'green_shade'), ml: { md: 2, xs: 1 }, my: { md: 0, xs: 2 } }}
              endIcon={<AddIcon />}
            >
              Create Proposal
            </Button>
          )}

          <Button
            variant="contained"
            color="error"
            onClick={handleRemove}
            size="large"
            sx={{ ...button('primary', 'primary_shade'), float: { md: 'right', xs: 'none' } }}
            disabled={userRole !== 'superadmin' && userRole !== 'admin'}
          >
            Delete Appointment
          </Button>
        </Grid>
      </Grid>

      {/* Proposal Type Selection Dialog */}
      <Dialog 
        open={proposalTypeDialogOpen} 
        onClose={handleCloseProposalTypeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          Select Proposal Type
        </DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            value={selectedProposalType}
            exclusive
            onChange={handleProposalTypeChange}
            aria-label="proposal type"
            fullWidth
            sx={{ mt: 1 }}
          >
            <ToggleButton 
              value="Installation" 
              aria-label="installation"
              sx={{
                py: 2,
                '&.Mui-selected': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                },
              }}
            >
              <HomeWorkIcon sx={{ mr: 1 }} />
              Installation
            </ToggleButton>
            <ToggleButton 
              value="Service" 
              aria-label="service"
              sx={{
                py: 2,
                '&.Mui-selected': {
                  backgroundColor: '#ed6c02',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#e65100',
                  },
                },
              }}
            >
              <BuildIcon sx={{ mr: 1 }} />
              Service
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            {selectedProposalType === 'Installation' 
              ? 'For equipment installation and replacement projects'
              : 'For maintenance and repair services'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseProposalTypeDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={createProposal} 
            variant="contained"
            sx={{ 
              backgroundColor: selectedProposalType === 'Installation' ? '#1976d2' : '#ed6c02',
              '&:hover': {
                backgroundColor: selectedProposalType === 'Installation' ? '#1565c0' : '#e65100',
              },
            }}
          >
            Create {selectedProposalType} Proposal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Reason Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => !cancelLoading && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          color: '#d32f2f',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <CancelIcon />
          Cancel Appointment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Please provide a reason for cancelling this appointment.
            This will be recorded in the appointment notes.
          </Typography>
          <TextField
            autoFocus
            label="Cancellation Reason"
            multiline
            rows={4}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            placeholder="Enter the reason for cancellation..."
            disabled={cancelLoading}
            error={!cancelReason.trim() && cancelReason.length > 0}
            helperText={!cancelReason.trim() && cancelReason.length > 0 ? "Reason cannot be empty" : ""}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            color="inherit"
            disabled={cancelLoading}
          >
            Back
          </Button>
          <Button 
            onClick={handleCancelWithReason} 
            variant="contained"
            color="error"
            disabled={!cancelReason.trim() || cancelLoading}
            startIcon={cancelLoading ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
          >
            {cancelLoading ? 'Cancelling...' : 'Cancel Appointment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion Rating Dialog */}
      <Dialog
        open={completionDialogOpen}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: '#ff9800' }} />
          {completionFromCheckout ? 'Check Out — Rate Client' : 'Complete Appointment — Rate Client'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            {completionFromCheckout
              ? 'Please rate this client before checking out.'
              : 'Please rate this client before completing the appointment.'}
          </Typography>

          {(() => {
            const selectedCp = (appointment?.property?.clients || []).find(cp => cp.client?.id === completionClientId);
            const selectedClient = selectedCp?.client;
            const existingRating = selectedClient?.rating || 0;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, p: 2, backgroundColor: '#fafafa', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {selectedClient?.fullName || 'Client'}
                </Typography>
                {existingRating > 0 && (
                  <Typography variant="caption" sx={{ mb: 1, color: '#999' }}>
                    Current rating: {existingRating}/5
                  </Typography>
                )}
                <Rating
                  value={completionRating}
                  onChange={(e, newValue) => setCompletionRating(newValue || 0)}
                  size="large"
                  sx={{ fontSize: 40 }}
                />
                <Typography variant="caption" sx={{ mt: 1, color: completionRating > 0 ? '#4caf50' : '#999' }}>
                  {completionRating === 0 ? 'Select a rating to continue' : `Rating: ${completionRating}/5`}
                </Typography>
              </Box>
            );
          })()}

          {(appointment?.property?.clients || []).length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="completion-client-label">Rate client</InputLabel>
              <Select
                labelId="completion-client-label"
                value={completionClientId}
                label="Rate client"
                onChange={(e) => {
                  const newId = e.target.value;
                  setCompletionClientId(newId);
                  const cp = (appointment?.property?.clients || []).find(c => c.client?.id === newId);
                  setCompletionRating(cp?.client?.rating || 0);
                }}
              >
                {(appointment?.property?.clients || []).map((cp) => (
                  <MenuItem key={cp.client?.id} value={cp.client?.id}>
                    {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                    {cp.client?.id === appointment?.contactClient?.id ? ' (preferred contact)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleCompletionSubmit}
            disabled={completionRating === 0 || completionSubmitting}
            startIcon={completionSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#388e3c' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
            }}
          >
            {completionSubmitting ? 'Processing...' : completionRating > 0
              ? (completionFromCheckout ? 'Check Out & Complete' : 'Complete Appointment')
              : 'Rate to Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Request Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: '#ff9800' }} />
          Request Review
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Send a Google review request via SMS to the selected client.
          </Typography>

          {(appointment?.property?.clients || []).length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="review-client-label">Send review to</InputLabel>
              <Select
                labelId="review-client-label"
                value={reviewClientId}
                label="Send review to"
                onChange={(e) => handleReviewClientChange(e.target.value)}
              >
                {(appointment?.property?.clients || []).map((cp) => (
                  <MenuItem key={cp.client?.id} value={cp.client?.id}>
                    {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                    {cp.client?.id === appointment?.contactClient?.id ? ' (preferred contact)' : ''}
                    {cp.client?.phoneNumber ? ` — ${cp.client.phoneNumber}` : ' (no phone)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {lastReviewLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ color: '#999' }}>Checking review history...</Typography>
            </Box>
          ) : lastReviewDate ? (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: canSendReview ? '#fff3e0' : '#ffebee', borderRadius: 2, border: `1px solid ${canSendReview ? '#ffe0b2' : '#ffcdd2'}` }}>
              <Typography variant="body2" sx={{ color: canSendReview ? '#e65100' : '#c62828', fontWeight: 500 }}>
                Last review request sent: {new Date(lastReviewDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
              {!canSendReview && (
                <Typography variant="caption" sx={{ color: '#c62828', display: 'block', mt: 0.5 }}>
                  Next request allowed after 6 months from the last one.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                No previous review requests for this client.
              </Typography>
            </Box>
          )}

          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>
              SMS preview:
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.6, color: '#333' }}>
              Airmaxx is family owned, so your feedback goes a long way! If you have a moment, please share your experience: https://g.page/r/CUnMmOhln4AMEBM/review If you have any concerns, please call 619-655-3010. Thank you!
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setReviewDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendReview}
            disabled={reviewSending || !canSendReview}
            startIcon={reviewSending ? <CircularProgress size={16} color="inherit" /> : <StarIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
            }}
          >
            {reviewSending ? 'Sending...' : 'Send Review Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <NearestTechModal
        open={nearestTechOpen}
        onClose={() => setNearestTechOpen(false)}
        appointmentId={id}
        address={appointment?.property?.address}
      />
    </Fragment>
  );
};

export default AppointmentDetailsTable;
